'use client';

import classNames from 'classnames';
import { useMemo } from 'react';
import {
  CcCardTrackingListName,
  CcProductCard,
} from '../../components/CcProductCard';
import { useProductDetailModal } from '../../hooks/useProductDetailModal';
import { resolveProductTag } from '../../utils/helpers';
import { applyProductFilter } from '../SortAndFilterPanel/applyProductFilter';
import { applyProductSort } from '../SortAndFilterPanel/applyProductSort';
import { FilterEmptyState } from '../SortAndFilterPanel/FilterEmptyState';
import { useProductFilterState } from '../SortAndFilterPanel/ProductFilterProvider';
import {
  FilterCategory,
  FilterSelection,
  optionKeyOf,
} from '../SortAndFilterPanel/productFilterTypes';
import {
  useActiveProductSort,
  useProductSortConfig,
} from '../SortAndFilterPanel/ProductSortProvider';
import { ProductSortOption } from '../SortAndFilterPanel/types';
import { CcCardListingProps } from './types';

// Whole class names, so Tailwind finds them.
const DESKTOP_COLUMNS = {
  2: classNames('lg:grid-cols-2'),
  3: classNames('lg:grid-cols-3'),
} as const;

// The lists' reporting names in the CMS-1902 tracking plan, the first part of every title.
const TRACKING_LIST_TITLES: Record<CcCardTrackingListName, string> = {
  RESULT_LIST: 'Result List',
  TRENDING_CARDS: 'Trending Cards',
  SIMILAR_CARDS: 'Similar Cards',
};

/**
 * The credit card product cards in a grid (BRD-01 FR-1.1). The cards are the page's
 * ProductMetaConfig, filtered and sorted by the Sort and Filter panel exactly as CardListingPosition
 * does, so a page carrying both shows one order. Below the desktop breakpoint the grid is one card
 * per row; on a row of cards each zone of the card lines up with its neighbours', so the cards share
 * one height and their CTA rows align (FR-1.1.4).
 */
export const CcCardListing: React.FC<CcCardListingProps> = ({
  listing,
  metadata,
  config,
  pushDataLayer,
  componentIndex,
  url,
  getPdpModalConfigAction,
  isPreview,
  renderPdpModalHero,
}) => {
  // Details opens in place through the one entry point (fe-services docs/product-details-page.md): the PDP
  // modal for a `#usePDP` product, the page's product panel otherwise.
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `CcCardListing_${listing.id}`,
    getPdpModalConfigAction,
    isPreview,
    renderPdpModalHero,
  });

  // A panel mounts the sort config even with its sort hidden, and it is page data, so the derived
  // column count is the same on the server and the client.
  const hasSortFilterPanel = !!useProductSortConfig();
  const cardsPerRow = listing.cards_per_row ?? (hasSortFilterPanel ? 2 : 3);

  const productSortOption = useActiveProductSort();
  const { categories, selection } = useProductFilterState();

  const cards = useMemo(
    () =>
      (metadata?.product_card ?? []).filter(
        (card) => card.visible !== false && card.productBlock?.product,
      ),
    [metadata?.product_card],
  );

  // Filter first, then order what survives: sorting must never bring back a filtered-out card.
  const orderedCards = useMemo(
    () =>
      applyProductSort(
        applyProductFilter(
          cards,
          categories,
          selection,
          (card) => card.productBlock?.product,
        ),
        productSortOption,
        (card) => card.productBlock?.product?.attributes,
        undefined,
        (card) => card.productBlock?.product,
      ),
    [cards, categories, selection, productSortOption],
  );

  // Only the Result List's events report the panel's state (#2490–#2493).
  const filterApplied =
    listing.tracking_list_name === 'RESULT_LIST'
      ? formatFilterApplied(categories, selection, productSortOption)
      : undefined;

  // Hidden below md, and from md back to the display each element needs: `md:block` on the grid
  // would turn it into a block and every card would take the full width.
  const hiddenOnMobile = !listing.visible_mobile;

  if (orderedCards.length === 0) {
    return (
      <div
        data-component="cc-card-listing-empty"
        className={classNames({ 'hidden md:block': hiddenOnMobile })}
      >
        <FilterEmptyState />
      </div>
    );
  }

  return (
    <>
      <div
        data-component="cc-card-listing"
        className={classNames(
          'grid grid-cols-1 gap-spacing/gap/soft-12px',
          { 'hidden md:grid': hiddenOnMobile },
          DESKTOP_COLUMNS[cardsPerRow],
        )}
      >
        {orderedCards.map((card, index) => {
          const product = card.productBlock?.product ?? {};

          return (
            <CcProductCard
              key={card.id}
              className={classNames({ 'hidden md:grid': !card.visible_mobile })}
              product={product}
              editorBadge={resolveProductTag({
                product,
                pageLevelTag: card.productBlock?.text_tag,
              })}
              badgeColor={card.productBlock?.color_tag}
              badgeIcon={card.productBlock?.icon_tag}
              ctaLinkOverride={card.productBlock?.override_cta_link}
              estimateConfig={config}
              tracking={
                pushDataLayer && {
                  pushDataLayer,
                  listTitle: TRACKING_LIST_TITLES[listing.tracking_list_name],
                  componentIndex: componentIndex ?? '',
                  index,
                  url,
                  filterApplied,
                }
              }
              onOpenDetails={
                product.url_alias ? () => openProductDetail(product) : undefined
              }
            />
          );
        })}
      </div>
      {productDetailModal}
    </>
  );
};

/**
 * `annual-fee-Free forever|issuer-HSBC,Citibank|sort-Trending`: each group with a selection by its
 * `category_id` and the labels the reader ticked, then the sort by its label — the same keys and
 * values the panel's own filter_action events send (CMS-1902).
 */
function formatFilterApplied(
  categories: readonly FilterCategory[],
  selection: FilterSelection,
  sort: ProductSortOption | undefined,
): string {
  const filters = categories.flatMap((category) => {
    const picked = category.categoryId ? selection[category.categoryId] : [];
    if (!picked?.length) return [];
    const labels = picked.map(
      (key) =>
        category.options.find((option) => optionKeyOf(option) === key)?.label ??
        key,
    );
    return [`${category.categoryId}-${labels.join(',')}`];
  });
  return [...filters, ...(sort ? [`sort-${sort.label}`] : [])].join('|');
}
